import type { SetupValue } from '../../data/types';
import { sectionErrors } from './setupRules';
import { ACTIVATION_SECTIONS, OFFER_FORM_SECTIONS } from './sections';

type Values = Record<string, SetupValue>;

/** Prototype-only fields the live wizard hides (account.userId is replaced by StaffContactPicker). */
export const LIVE_HIDDEN_KEYS = new Set([
  'account.userId',
  'offer.publicApply',
  'offer.priorityCodes',
  'offer.priorityList',
  'class.appRounding',
  'class.asxCode',
  'class.capTableOrder',
  'class.excludeStatements',
]);

export type SetupAttempt = 'activate' | 'publish';

const text = (values: Values, key: string) => String(values[key] ?? '').trim();

/** The ATO's ABN check, as the server applies it: take 1 from the first digit, weight, divide by 89. */
export function isValidAbn(abn: string): boolean {
  const digits = abn.replace(/\s/g, '').split('').map(Number);
  if (digits.length !== 11 || digits.some((digit) => Number.isNaN(digit))) return false;
  digits[0] -= 1;
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  return digits.reduce((total, digit, index) => total + digit * weights[index], 0) % 89 === 0;
}

/** Today's date and time (YYYY-MM-DD, HH:MM) on a time zone's wall clock. */
function wallClock(timeZone: string, now: Date) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(now)
      .map((part) => [part.type, part.value]),
  );
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    time: `${parts.hour}:${parts.minute}`,
  };
}

/** True once the offer's close date and time have passed in its time zone. */
export function closePassed(values: Values, now: Date): boolean {
  const closeDate = text(values, 'offer.closeDate');
  if (!closeDate) return false;
  const closeTime = text(values, 'offer.closeTime') || '23:59';
  const today = wallClock(text(values, 'offer.timezone') || 'Australia/Melbourne', now);
  return closeDate < today.date || (closeDate === today.date && closeTime <= today.time);
}

/** The setup's rules plus the server's extra checks (ABN checksum, close date passed). */
export function liveSectionErrors(
  sectionId: string,
  values: Values,
  now: Date = new Date(),
): Record<string, string> {
  const errors = Object.fromEntries(
    Object.entries(sectionErrors(sectionId, values)).filter(([key]) => !LIVE_HIDDEN_KEYS.has(key)),
  );
  const abn = text(values, 'fund.abn');
  if (sectionId === 'trust' && abn && !errors['fund.abn'] && !isValidAbn(abn)) {
    errors['fund.abn'] = "This isn't a valid ABN; check the digits";
  }
  if (sectionId === 'offer' && !errors['offer.closeDate'] && closePassed(values, now)) {
    errors['offer.closeDate'] = 'The close date has passed; choose a later date';
  }
  return errors;
}

/** Steps whose required fields are marked once activation or sending for approval was tried. */
export function attemptedSections(attempts: SetupAttempt[]): string[] {
  return [
    ...(attempts.includes('activate') ? ACTIVATION_SECTIONS : []),
    ...(attempts.includes('publish') ? OFFER_FORM_SECTIONS : []),
  ];
}
